import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import api from "../../../service";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import {
  completeCourseSuccess,
  lessonLikeSuccess,
  lessonUnlikeSuccess,
  markLessonCompletedSuccess,
  restartCourseSuccess,
} from "../../../redux/modules/informaAcademy/actions";
import useNotification from "../../../hooks/newUXNotification";
import { useIntl } from "react-intl";

export function useAcademy() {
  const { courseId, lessonId } = useParams();
  const { event } = useSelector((state) => state.initialSettings);
  const { token } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const notification = useNotification();
  const intl = useIntl();

  const options = useMemo(
    () => ({
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  );

  const completeLesson = useCallback(async () => {
    try {
      await api.put(
        `/academy/event/${event.value}/course/${courseId}/lesson/${lessonId}/completed`,
        options
      );
      return dispatch(markLessonCompletedSuccess(lessonId));
    } catch (error) {
      console.error(`Error: ${error.message}`);
      notification.displayError(
        intl.formatMessage({ id: "lesson_complete_error" })
      );
    }
  }, [courseId, dispatch, event.value, intl, lessonId, notification, options]);

  const restartCourse = useCallback(async () => {
    try {
      await api.put(
        `/academy/event/${event.value}/course/${courseId}/restart`,
        options
      );
      return dispatch(restartCourseSuccess());
    } catch (error) {
      console.error(`Error: ${error.message}`);
      notification.displayError(
        intl.formatMessage({ id: "course_restart_error" })
      );
    }
  }, [courseId, dispatch, event.value, intl, notification, options]);

  const completeCourse = useCallback(async () => {
    try {
      await api.put(
        `/academy/event/${event.value}/course/${courseId}/complete`,
        options
      );

      const body = JSON.stringify({
        courseId,
        action: "VIEWS",
        eventId: event.value,
      });
      await api.post("/academy/analytics/courses", body, options);

      return dispatch(completeCourseSuccess());
    } catch (error) {
      console.error(`Error: ${error.message}`);
      notification.displayError(
        intl.formatMessage({ id: "course_complete_error" })
      );
    }
  }, [courseId, dispatch, event.value, intl, notification, options]);

  const postBannerAnalyticsAction = useCallback(
    async ({ bannerId, action }) => {
      const body = JSON.stringify({
        bannerId: parseInt(bannerId),
        action,
      });
      await api.post("/academy/analytics/banners", body, options);
    },
    [options]
  );

  const postCourseAnalyticsAction = useCallback(
    async ({ courseId, action }) => {
      const body = JSON.stringify({
        courseId: parseInt(courseId),
        action,
        eventId: event.value,
      });
      await api.post("/academy/analytics/courses", body, options);
    },
    [event.value, options]
  );

  const postLessonAnalyticsAction = useCallback(
    async ({ action }) => {
      const body = JSON.stringify({
        courseId: parseInt(courseId),
        lessonId: parseInt(lessonId),
        action,
        eventId: event.value,
      });
      await api.post("/academy/analytics/courses", body, options);
    },
    [courseId, event.value, lessonId, options]
  );

  const lessonAnalyticsRecommendation = useCallback(async () => {
    await postLessonAnalyticsAction({ action: "RECOMMENDATION" });
    return dispatch(lessonLikeSuccess());
  }, [dispatch, postLessonAnalyticsAction]);

  const deleteLessonAnalyticsRecommendation = useCallback(async () => {
    await api.delete(
      `/academy/analytics/courses/recommendation/${event.value}/${courseId}/${lessonId}`,
      options
    );

    return dispatch(lessonUnlikeSuccess());
  }, [courseId, dispatch, event.value, lessonId, options]);

  return {
    completeLesson,
    restartCourse,
    completeCourse,
    postBannerAnalyticsAction,
    postCourseAnalyticsAction,
    postLessonAnalyticsAction,
    lessonAnalyticsRecommendation,
    deleteLessonAnalyticsRecommendation,
  };
}
